// Phase 1 of backend refactor (BACKEND_REFACTOR_WORKFLOW.md):
// promiseWorker is now a WebSocket client to a Node.js server, exposing the same API so
// toWorker.ts, src/ui/index.tsx, and all 101 toWorker call sites are unchanged.
//
// Exposed surface (matches PWBHost):
//   - postMessage([type, name, param]) → Promise<result>
//   - register(fn)           // server → client push (corresponds to toUI)
//   - registerError(fn)      // error callback

import {
	parse as parseWs,
	stringify as stringifyWs,
} from "../../../../common/structuredCloneJSON.ts";
import {
	DEFAULT_USER_ID,
	WS_PATH,
	type WsAuth,
	type WsCall,
	type WsPush,
	type WsServerMessage,
} from "../../../../common/wsProtocol.ts";
import {
	clearToken,
	getOrCreateDeviceId,
	loadStoredToken,
	saveToken,
} from "./auth.ts";
import { local } from "./local.ts";

const RECONNECT_DELAY_MS = 1000;

const getServerUrl = (): string => {
	const override = (window as any).bbgmServerUrl as string | undefined;
	if (override) {
		return override;
	}
	const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
	return `${proto}//${window.location.host}${WS_PATH}`;
};

type PendingEntry = {
	resolve: (value: unknown) => void;
	reject: (reason: unknown) => void;
};

// `data` is the array passed by the server in a WsPush (e.g. [name, ...args]).
// Typed as `any` to match the original PWBHost ergonomic shape: callers
// destructure it with their own tuple type.
type RegisterHandler = (data: any) => any;
type ErrorHandler = (error: Error) => void;

class WebSocketRPC {
	private ws: WebSocket | null = null;
	private nextId = 1;
	private pending = new Map<number, PendingEntry>();
	private outbox: WsCall[] = [];
	private pushHandler: RegisterHandler | null = null;
	private errorHandler: ErrorHandler | null = null;
	private ready = false;
	// Phase 11.7 + 11.8: both deviceId and session token come from
	// `src/ui/util/auth.ts`, which talks to localStorage in the Capacitor
	// WebView and in the browser debug harness.
	// Token at construction time is the one persisted from a prior
	// page lifetime — null on truly first launch.
	private sessionToken: string | null = loadStoredToken();
	private readonly deviceId: string = getOrCreateDeviceId();

	constructor() {
		this.connect();
		this.setupZombieGuard();
	}

	// Mobile WebViews (Capacitor + iOS Safari + Android Chrome) suspend JS when
	// the app is backgrounded. The OS may keep the TCP socket alive
	// (ws.readyState stays OPEN) but server pushes sent while suspended can be
	// dropped, leaving a "zombie" socket where outbound calls silently vanish
	// — the user sees a frozen UI even though the connection "looks" healthy.
	// On foreground, force a reconnect so any pending requests fail loudly
	// (close handler rejects them) and new calls land on a live socket.
	// Desktop tab-switches are brief and don't suffer this, so skip the cost.
	private setupZombieGuard() {
		if (typeof document === "undefined") {
			return;
		}
		if (!(window as any).mobile) {
			return;
		}
		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState !== "visible") {
				return;
			}
			const ws = this.ws;
			if (!ws) {
				return;
			}
			try {
				ws.close();
			} catch {
				// Already closing — the close handler still schedules
				// reconnect, so this branch is harmless.
			}
		});
	}

	private connect() {
		const url = getServerUrl();
		const ws = new WebSocket(url);
		this.ws = ws;

		ws.addEventListener("open", () => {
			// Path B (resume) if we still have an in-memory session token;
			// Path A (signin/signup by device) otherwise. Server prefers
			// token if both are present — see SqliteAuthHandler.handle.
			const authFrame: WsAuth = this.sessionToken
				? { kind: "auth", token: this.sessionToken }
				: { kind: "auth", deviceId: this.deviceId };
			ws.send(stringifyWs(authFrame));
		});

		ws.addEventListener("message", (ev) => {
			let msg: WsServerMessage;
			try {
				msg = parseWs(ev.data as string) as WsServerMessage;
			} catch (err) {
				console.error("Bad WS message from server:", ev.data, err);
				return;
			}

			// --- Phase 11.2 + 11.7/11.8: auth handshake responses ---
			if (msg.kind === "authOk") {
				this.sessionToken = msg.token;
				saveToken(msg.token);
				this.ready = true;
				while (this.outbox.length > 0) {
					const next = this.outbox.shift();
					if (next) {
						ws.send(stringifyWs(next));
					}
				}
				return;
			}

			if (msg.kind === "authFail") {
				// Token rejected (likely server restart, or the user record
				// was dropped). Drop the stale token from BOTH memory and
				// storage so the close-then-reconnect cycle goes through
				// device-credential signup, not a doomed retry loop.
				console.warn("[ws] auth failed:", msg.message, "— retrying");
				this.sessionToken = null;
				clearToken();
				try {
					ws.close();
				} catch {
					// close() throws if the socket is already closing — fine,
					// the close handler will schedule the reconnect.
				}
				return;
			}

			if (msg.kind === "result") {
				const entry = this.pending.get(msg.id);
				if (entry) {
					this.pending.delete(msg.id);
					entry.resolve(msg.result);
				}
				return;
			}

			if (msg.kind === "error") {
				const entry = this.pending.get(msg.id);
				const error = new Error(msg.message);
				if (msg.stack) {
					error.stack = msg.stack;
				}
				if (entry) {
					this.pending.delete(msg.id);
					entry.reject(error);
				} else if (this.errorHandler) {
					this.errorHandler(error);
				}
				return;
			}

			if (msg.kind === "push") {
				// Drop pushes from a league other than the one the URL is
				// currently viewing. The server's `subscribeConnection` is
				// monotonic — every dispatch auto-subscribes and there's no
				// unsubscribe-on-navigate — so after the UI flips from
				// league A to league B, the WS is still subscribed to A
				// and A's worker thread keeps pushing `realtimeUpdate` /
				// `updateLocal` / etc. Without this filter, those land in
				// the local store and contaminate the B view.
				//
				// We CAN'T gate on `local.lid` here: `local.lid` itself is
				// updated by setGameAttributes pushes from the new league.
				// While entering league B, every push from B carries
				// `msg.lid=B` but `local.lid` is still A (or null) until
				// one of those pushes lands — and a `local.lid`-based
				// filter would drop them all, so lid never sets. Same race
				// fixed for the `runBefore` RPC: pull the target lid from
				// the URL (react-router populates `/l/:lid/...`), not from
				// racing local state.
				let urlLid: number | null = null;
				const m = /^\/l\/(\d+)(?:[/?#]|$)/.exec(
					window.location.pathname,
				);
				if (m && m[1]) {
					const parsed = Number.parseInt(m[1]);
					if (!Number.isNaN(parsed)) {
						urlLid = parsed;
					}
				}
				if (msg.lid !== null && msg.lid !== urlLid) {
					this.replyToPushError(
						msg,
						new Error(
							`Ignoring push for lid ${msg.lid}; current URL lid is ${urlLid}`,
						),
					);
					return;
				}
				if (!this.pushHandler) {
					this.replyToPushError(msg, new Error("No push handler registered"));
					return;
				}

				Promise.resolve()
					.then(() => this.pushHandler!([msg.name, ...msg.args]))
					.then((result) => this.replyToPushResult(msg, result))
					.catch((err) => {
						console.error("Error in server push handler:", err);
						this.replyToPushError(msg, err);
					});
			}
		});

		ws.addEventListener("close", () => {
			this.ready = false;
			this.ws = null;
			// Reject all in-flight requests. Without this they would hang
			// forever — the original WS that they were sent on is gone and
			// the server won't reply, even after reconnect (request IDs
			// don't survive). Caller code typically retries via user action.
			if (this.pending.size > 0) {
				const err = new Error("WebSocket disconnected before reply");
				for (const entry of this.pending.values()) {
					entry.reject(err);
				}
				this.pending.clear();
			}
			setTimeout(() => this.connect(), RECONNECT_DELAY_MS);
		});

		ws.addEventListener("error", () => {
			if (this.errorHandler) {
				this.errorHandler(new Error("WebSocket connection error"));
			}
		});
	}

	postMessage<R = unknown>(
		payload: [string, string | number | symbol, unknown],
	): Promise<R> {
		const [type, name, param] = payload;
		const id = this.nextId++;
		// Resolve the "active league" at send time, not at construction time:
		// the user may navigate between leagues during the lifetime of this
		// singleton. `local.getState().lid` is `undefined` when no league is
		// open (system pages like dashboard / new league); convert to `null`
		// per the protocol contract.
		//
		// Special case: `main.beforeView` is the call that transitions the UI
		// from "no league" into a league (or between leagues). It's sent
		// *before* `local.lid` is updated, so reading `local.lid` here would
		// dispatch the call against the previous league's worker thread,
		// which is wrong: the call's purpose is to open `lidUrl`. Pull the
		// target lid from the call's payload instead.
		let lid: number | null;
		if (type === "main" && name === "beforeView") {
			const lidUrl = (param as { lidUrl?: unknown } | null | undefined)
				?.lidUrl;
			if (typeof lidUrl === "number") {
				lid = lidUrl;
			} else if (lidUrl === undefined) {
				// Leaving a league for dashboard / new-league: target is
				// "no league", which is `null` (= system RPC).
				lid = null;
			} else {
				// Non-numeric, non-undefined lidUrl — log so we notice if a future
				// caller introduces a shape this routing can't handle, but route to
				// system as the safest fallback.
				console.warn(
					"[promiseWorker] beforeView with non-numeric lidUrl",
					lidUrl,
				);
				lid = null;
			}
		} else if (type === "main" && name === "runBefore") {
			// Same race as beforeView: runBefore fires immediately after the
			// awaited beforeView resolves, but `local.lid` is updated
			// asynchronously by route-change subscribers — which may not have
			// caught up yet. Reading local.lid here would route to the
			// previous league's worker (or system) and crash inside views
			// that touch idb.cache / idb.league.
			//
			// Pull lid from the route's URL params (react-router populates
			// these from /l/:lid/...). System views (/, /new_league, ...) have
			// no `lid` param → null → system RPC, which is correct.
			const routeParams = (
				param as { params?: { lid?: unknown } } | null | undefined
			)?.params;
			const rawLid = routeParams?.lid;
			if (typeof rawLid === "string") {
				const parsed = Number.parseInt(rawLid);
				lid = Number.isNaN(parsed) ? null : parsed;
			} else if (typeof rawLid === "number") {
				lid = rawLid;
			} else {
				lid = null;
			}
		} else {
			lid = local.getState().lid ?? null;
		}
		const msg: WsCall = {
			id,
			kind: "call",
			userId: DEFAULT_USER_ID,
			lid,
			type: type as WsCall["type"],
			name: String(name),
			param,
		};
		return new Promise<R>((resolve, reject) => {
			this.pending.set(id, {
				resolve: resolve as (v: unknown) => void,
				reject,
			});
			if (this.ready && this.ws) {
				this.ws.send(stringifyWs(msg));
			} else {
				this.outbox.push(msg);
			}
		});
	}

	register(fn: RegisterHandler) {
		this.pushHandler = fn;
	}

	private replyToPushResult(msg: WsPush, result: unknown) {
		if (msg.id === undefined || !this.ws || !this.ready) {
			return;
		}
		this.ws.send(
			stringifyWs({
				id: msg.id,
				kind: "pushResult",
				lid: msg.lid,
				result,
			}),
		);
	}

	private replyToPushError(msg: WsPush, err: unknown) {
		if (msg.id === undefined || !this.ws || !this.ready) {
			return;
		}
		const error = err instanceof Error ? err : new Error(String(err));
		this.ws.send(
			stringifyWs({
				id: msg.id,
				kind: "pushError",
				lid: msg.lid,
				message: error.message,
				...(error.stack ? { stack: error.stack } : {}),
			}),
		);
	}

	registerError(fn: ErrorHandler) {
		this.errorHandler = fn;
	}
}

export const promiseWorker = new WebSocketRPC();
promiseWorker.registerError((error) => {
	console.error("Error from server:");
	console.error(error);
});
